import axios from "axios";
import { getServerValidation } from "./helperFunctions";
import { API_URL, iamUrl } from "../Utils/config";
import { ToastContainer, toast } from "react-toastify";
import { loginUrl } from "./constant";
toast.configure();

const refreshToken = async () => {
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const axiosForRefresh = axios.create({ withCredentials: true });
  const response = await axiosForRefresh.post(
    iamUrl + "/iam/v1.0/token/refresh",
    {
      employee_code: userData?.adminEmployeeId,
      channel: "CENTRAL_ADMIN_UI",
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    }
  );
  return response?.data?.access_token || null;
};

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  (config) => {
      config.withCredentials = true;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // Handle token refresh for 401/403 errors
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        await refreshToken();
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem("userData");
        // Redirect to the new Central Admin login page instead of unified-login-ui
        window.location.href = `/#/login`;
        return Promise.reject(refreshError);
      }
    }
    // Don't show toast here - let handleError function handle all error toasts
    // This prevents duplicate toasts. All errors will be handled by handleError
    // which is called in the .catch() of each HTTP method (get, post, put, etc.)
    return Promise.reject(error);
  }
);

const handleResponse = (response) => {
  return {
    status: response.status,
    data: response.data,
  };
};

const handleResponseFile = async (response) => {
  try {
    const contentType = response.headers["content-type"];
    if (contentType?.includes("application/json")) {
      const text = await response.data.text();
      return JSON.parse(text);
    }
    return response;
  } catch {
    return {
      status: response?.status || 500,
      message: "Failed to parse response",
    };
  }
};
const handleError = (error) => {
  const { response } = error;
  let parsedError = response && JSON.parse(JSON.stringify(response.data));
  let errorMsg = "";
  let shouldShowToast = true;
  
  // Handle case when response is undefined (network error, service unavailable)
  if (response == undefined) {
    errorMsg = "Service Unavailable";
  } else if (response.status == 401) {
    // Don't show toast for 401 - redirect to login
    localStorage.clear();
    window.location.href = loginUrl;
    shouldShowToast = false;
  } else if (
    response &&
    response != undefined &&
    JSON.parse(JSON.stringify(response.data)).toString().includes("401")
  ) {
    // Handle 401 in response body
    let element = document.getElementById("unauthorized-box");
    if (element) {
      element.style.display = "block";
      errorMsg = null;
    }
    localStorage.removeItem("userData");
    window.location.href = `${loginUrl}`;
    shouldShowToast = false;
  } else if (response && response.status == 402) {
    errorMsg = response.data.message;
  } else if (
    response &&
    response.status === 404 &&
    response.data &&
    response.data.message
  ) {
    errorMsg = response.data.message;
  } else if (response && response != undefined && response.status === 422) {
    errorMsg = "Sorry, something went wrong. Please try again.";
    if (response.data && response.data.errors)
      errorMsg = getServerValidation(response.data.errors) || errorMsg;
    else if (response.data.message) {
      errorMsg = response.data.message;
    }
  } else if (response?.data?.message) {
    errorMsg = response.data.message;
  } else if (response?.data?.errorMessage) {
    errorMsg = response.data.errorMessage;
  } else {
    errorMsg = "Sorry, something went wrong. Please try again.";
  }
  
  // Show toast only once, and only if we should show it
  if (shouldShowToast && errorMsg) {
    toast.error(errorMsg, {
      type: toast.TYPE.ERROR,
      autoClose: 5000, // Increased duration to 5 seconds
    });
  }
  
  return {
    error: true,
    message: response?.data?.message
      ? response.data.message
      : response?.data?.errorMessage
      ? response.data.errorMessage
      : null,
    status: response && response != undefined ? response.status : null,
  };
};

export const get = (
  url,
  isPrivate = true,
  responseType = null,
  customUrl = false,
  headers
) => {
  let apiUrl = API_URL + url;
  if (customUrl) {
    apiUrl = url;
  }
  const axiosObj = {
    method: "get",
    url: apiUrl,
  };
  if (headers) {
    axiosObj.headers = headers;
  }
  if (responseType) axiosObj.responseType = responseType;
  return axiosInstance(axiosObj)
    .then((response) => handleResponse(response))
    .catch((error) => handleError(error));
};

export const post = (
  url,
  bodyObj = {},
  isPrivate = true,
  mediaFile = false,
  uat = false
) => {
  const apiUrl = !uat ? API_URL + url : url;
  let header = {};
  if (mediaFile == true) {
    header = {
      "Access-Control-Allow-Origin": "*",
      "Content-Type": "multipart/form-data",
    };
  }
  return axiosInstance
    .post(apiUrl, bodyObj, {
      headers: header,
    })
    .then((response) => {
      return handleResponse(response);
    })
    .catch((error) => {
      return handleError(error);
    });
};

export const put = (
  url,
  bodyObj = {},
  isPrivate = true,
  mediaFile = false,
  uat = false
) => {
  const apiUrl = !uat ? API_URL + url : url;
  let header = {};
  if (mediaFile == true) {
    header = {
      "Access-Control-Allow-Origin": "*",
      "Content-Type": "multipart/form-data",
    };
  }
  return axiosInstance
    .put(apiUrl, bodyObj, {
      headers: header,
    })
    .then((response) => {
      return handleResponse(response);
    })
    .catch((error) => {
      return handleError(error);
    });
};

export const postdelete = (
  url,
  isPrivate = true,
  mediaFile = false,
  uat = false
) => {
  const apiUrl = !uat ? API_URL + url : url;
  let header = {};
  if (mediaFile == true) {
    header = {
      "Access-Control-Allow-Origin": "*",
      "Content-Type": "multipart/form-data",
    };
  }
  return axiosInstance
    .delete(apiUrl, {
      headers: header,
    })
    .then((response) => {
      return handleResponse(response);
    })
    .catch((error) => {
      return handleError(error);
    });
};

export const postFile = (apiURL, bodyObj = {}, contentType) => {
  return axiosInstance({
    method: "post",
    url: `${API_URL}${apiURL}`,
    data: bodyObj,
    config: { headers: { "Content-Type": "multipart/form-data" } },
  })
    .then((response) => handleResponse(response))
    .catch((error) => handleError(error));
};

export const awspost = (
  url,
  bodyObj = {},
  isPrivate = true,
  mediaFile = false,
  uat = false
) => {
  const apiUrl = !uat ? API_URL + url : url;

  if (mediaFile == true) {
    let formData = new FormData();
    Object.keys(bodyObj).map((key) => {
      formData.append(key, bodyObj[key]);
    });
    bodyObj = formData;
  }
  return axiosInstance
    .post(apiUrl, bodyObj, {
      headers: {
        "x-api-key": "dlQFSd7b2i3lnWLhMwmPx2Uv3wqU6zHGxwnxwFy9",
        Accept: "application/json",
        "Content-Type": "application/json",
        Origin: "*",
        "Access-Control-Allow-Headers": "*",
        "Access-Control-Allow-Origin": "*",
      },
    })
    .then((response) => handleResponse(response))
    .catch((error) => handleError(error));
};
export const postexcel = (url, data) => {
  const api_url = API_URL + url;
  return axiosInstance
    .post(api_url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      responseType: "blob",
    })
    .then(handleResponseFile)
    .catch(async (err) => {
      if (err.response?.data instanceof Blob)
        return await handleResponseFile(err.response);
      return { status: err.response?.status || 500, message: "Unknown error" };
    });
};
